import React, {FC, memo, useEffect, useRef, useState} from "react";
import {Button, Card, Divider, Form, Input, Modal, Tag, Typography, message} from "antd";
import {CheckOutlined, CloseCircleOutlined, PlusOutlined} from "@ant-design/icons";
import {blue, red} from "@ant-design/colors";
import {PossibleFormikBookingProps} from "../BookingCreate";
import {CurrencyShort, renderAmount} from "../../../../helpers/renderAmount";
import {Currency, ScheduledPaymentType} from "../../../../generated/graphql";
import {useCurrency} from "../../../../helpers/currencyHelper";
import {schadow3} from "../../../../styles/layout";
import MoneyInput from "../../../../views/MoneyInput/MoneyInput";
import css from "./BookingPricePreview.less";

type BookingDiscountProps = {
    formikProps: PossibleFormikBookingProps;
    regenerateSchedule: (params: {amountToCredit?: number; resetCredit?: boolean; discount?: number; discountName?: string}) => Promise<void>;
};

/**
 * Berechnet den maximal zulässigen (weiteren) Rabattbetrag.
 * grossPrice ist bereits nach Abzug von Rabatt, Guthaben und Mitgliederrabatt berechnet, enthält aber feste Posten
 * (Zusatzposten, Gebühren), die der Server nie kürzt. Deshalb zusätzlich gegen die Raten deckeln, von denen er abzieht
 * (Standard, ersatzweise Custom, wie splitPaymentsForPricing); sonst stünde auf der Rechnung ein zu hoher Rabatt.
 */
const getMaxDiscount = (formikProps: PossibleFormikBookingProps): number => {
    const grossPrice = formikProps.values.grossPrice ?? 0;
    const rates = formikProps.values.scheduledPayments ?? [];
    const standard = rates.filter((rate) => rate?.type === ScheduledPaymentType.Standard);
    const discountable = standard.length > 0 ? standard : rates.filter((rate) => rate?.type === ScheduledPaymentType.Custom);

    if (discountable.length === 0) {
        return Math.max(0, grossPrice);
    }

    const discountableSum = discountable.reduce((sum, rate) => sum + Number(rate?.paymentAmount ?? 0), 0);

    return Math.max(0, Math.min(grossPrice, Math.round(discountableSum * 100) / 100));
};

// Auch für den Einzelbuchungs-Editor (LessonBookingDiscount): Speichern ohne den Code-Rabatt löst die Einlösung.
export const confirmRemoveCodeDiscount = (discountName: string, onOk: () => unknown) =>
    Modal.confirm({
        title: "Rabattcode-Rabatt entfernen?",
        content: `Nach dem Speichern zählt die Buchung nicht mehr als Einlösung von „${discountName || "Rabattcode"}“. Bei einer Höchstzahl wird der Platz wieder frei.`,
        okText: "Entfernen",
        cancelText: "Abbrechen",
        onOk,
    });

// booking.discountName ist varchar(100); länger scheitert das Speichern an der DB.
const DISCOUNT_NAME_MAX_LENGTH = 100;

// Gespeicherter Text steht auf Rechnungen: feste Schreibweise je Währung statt der Browser-Locale der Mitarbeiterin.
const formatPartAmount = (amount: number, currency: CurrencyShort) => renderAmount({amount, currency, localeOverwrite: currency === "CHF" ? "de-CH" : "de-DE"});

const withAmount = (name: string, amount: number, currency: CurrencyShort) => `${name} (${formatPartAmount(amount, currency)})`;

// Endet die Bezeichnung auf einen Betrag mit Währung in Klammern, wurde sie schon mit Beträgen zusammengesetzt.
// „Geschwister (10 %)“ oder „Kulanz (Ausfall 12.10.)“ zählen nicht: ohne Währungszeichen und zwei Nachkommastellen.
const hasAmountSuffix = (name: string) => {
    const last = /\(([^()]*)\)$/.exec(name)?.[1] ?? "";

    return /(CHF|€|\$|£)/.test(last) && /\d[.,]\d{2}/.test(last);
};

const stackedCurrentPart = (current: {discount: number; discountName: string}, currency: CurrencyShort) => {
    const name = current.discountName || "Rabatt";

    if (hasAmountSuffix(name)) {
        return name;
    }
    // Vor den Einzelbeträgen (#1627) verbundene Namen „A + B“ lassen sich nicht aufteilen: Summe als solche kennzeichnen.
    return name.includes(" + ") ? `${name} (zusammen ${formatPartAmount(current.discount, currency)})` : withAmount(name, current.discount, currency);
};

/** Platz für die Bezeichnung des weiteren Rabatts, damit „bisher (Betrag) + neu (Betrag)“ in varchar(100) passt; Betrag höchstens `maxAmount`. */
export const remainingDiscountNameLength = (currentDiscount: number, currentDiscountName: string, maxAmount: number, currency: CurrencyShort) => {
    if (currentDiscount <= 0) {
        return DISCOUNT_NAME_MAX_LENGTH;
    }
    const used = stackedCurrentPart({discount: currentDiscount, discountName: currentDiscountName}, currency).length + " + ".length + withAmount("", maxAmount, currency).length;

    return Math.max(1, DISCOUNT_NAME_MAX_LENGTH - used);
};

/**
 * Eine Buchung hat genau einen Rabatt (Betrag + Bezeichnung). Ein weiterer Rabatt, etwa ein Gutschein nach der
 * anteiligen Umbuchung, wird aufaddiert und die Bezeichnungen verbunden, jede mit ihrem Betrag, damit Rechnung
 * und Vorkasse-PDF die Teile zeigen (aqua-vision). Gerechnet wird wie beim Löschen und Neueingeben der Summe;
 * Rechnung, Storno und Umbuchung sehen nur den Gesamtbetrag. Beträge werden nie abgeschnitten: reicht der Platz
 * nicht, wird der neue Name gekürzt, und passt nicht einmal sein Betrag, steht nur noch „Rabatte“ (Summe in der Zeile).
 */
export const stackDiscount = (current: {discount: number; discountName: string}, added: {discount: number; discountName: string}, currency: CurrencyShort) => {
    const addedName = added.discountName.trim() || "Rabatt";

    if (current.discount <= 0) {
        return {discount: added.discount, discountName: addedName.slice(0, DISCOUNT_NAME_MAX_LENGTH)};
    }
    const discount = Math.round((current.discount + added.discount) * 100) / 100;
    const head = `${stackedCurrentPart(current, currency)} + `;
    const room = DISCOUNT_NAME_MAX_LENGTH - head.length - withAmount("", added.discount, currency).length;

    return {discount, discountName: room >= 1 ? head + withAmount(addedName.slice(0, room), added.discount, currency) : "Rabatte"};
};

// Fall B 86391: Wer eine Rate von Hand ändert, setzt den Betrag direkt (Gesamtpreis = Summe der Raten).
// Rabatt und Guthaben werden dann nicht noch einmal abgezogen; die Zeile darüber ist nur noch Ausweis.
// Für den Rabatt zeigt das inzwischen der Preisblock (PriceBreakdown), BookersCredit nutzt den Hinweis weiter.
export const IncludedInRatesHint = () => (
    <Typography.Text type="secondary" style={{fontSize: 12}}>
        {"Bereits in den Raten enthalten."}
    </Typography.Text>
);

const BookingDiscount: FC<BookingDiscountProps> = ({formikProps, regenerateSchedule}) => {
    const [cardOpen, setCardOpen] = useState(false);
    const isMountedRef = useRef(true);
    const currency = useCurrency();

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const currentDiscount = formikProps.values.discount ?? 0;
    const currentDiscountName = formikProps.values.discountName ?? "";
    // Nur beim Bearbeiten gesetzt (getBookingById). Speichern ohne den Code-Rabatt löst die Verknüpfung.
    const linkedDiscountCode = Boolean((formikProps.values as {hasDiscountCode?: boolean | null}).hasDiscountCode);

    const removeDiscount = () => regenerateSchedule({discount: 0, discountName: ""});

    const DiscountCard = () => {
        const [discountAmount, setDiscountAmount] = useState<number>(0);
        const [discountName, setDiscountName] = useState<string>("");

        const maxDiscount = getMaxDiscount(formikProps);

        return (
            <Card
                title={currentDiscount > 0 ? "Weiteren Rabatt hinzufügen" : "Rabatt hinzufügen"}
                extra={<Button type="text" size="small" icon={<CloseCircleOutlined style={{color: red[3]}} />} onClick={() => setCardOpen(false)} />}
                style={{
                    padding: 7,
                    paddingBottom: 0,
                    width: "100%",
                    boxShadow: schadow3,
                    borderRadius: 7,
                    border: `2px solid ${blue[3]}`,
                }}
            >
                <div>
                    <Form.Item label="Bezeichnung" style={{marginBottom: 8}}>
                        <Input placeholder="Rabatt" maxLength={remainingDiscountNameLength(currentDiscount, currentDiscountName, maxDiscount, currency.short as CurrencyShort)} value={discountName} onChange={(e) => setDiscountName(e.target.value)} />
                    </Form.Item>
                    <Divider type="horizontal" style={{margin: 5}} />
                    <Form.Item>
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gridColumnGap: 10,
                            }}
                        >
                            <MoneyInput onChange={(e) => setDiscountAmount(e ?? 0)} />
                            <Button
                                style={{gridColumn: "span 1"}}
                                disabled={!discountAmount || discountAmount <= 0}
                                onClick={async () => {
                                    const cappedAmount = Math.min(discountAmount, maxDiscount);

                                    if (cappedAmount <= 0) {
                                        message.warning("Der Rabatt kann nicht angewendet werden, da der verbleibende Betrag bereits 0 ist.");
                                        return;
                                    }
                                    if (cappedAmount < discountAmount) {
                                        message.info(`Rabatt wurde auf den maximal möglichen Betrag begrenzt.`);
                                    }
                                    // WP1: discount/discountName NICHT mehr optimistisch vor
                                    // regenerateSchedule setzen — sonst bliebe bei "Abbrechen" im
                                    // (neuen) Vorkasse-Bestätigungsdialog ein halber Formik-State
                                    // (neuer Rabatt, alte Raten). regenerateSchedule schreibt beide
                                    // Felder erst nach erfolgreicher Neuberechnung (handleFormikPropsUpdate);
                                    // die Mutation erhält den Rabatt ohnehin über den expliziten Parameter.
                                    // maxDiscount ist der kürzbare Rest nach dem bisherigen Rabatt; der neue kommt obendrauf.
                                    await regenerateSchedule(
                                        stackDiscount({discount: currentDiscount, discountName: currentDiscountName}, {discount: cappedAmount, discountName}, currency.short as CurrencyShort),
                                    );
                                    if (isMountedRef.current) {
                                        setCardOpen(false);
                                    }
                                }}
                            >
                                {"Anwenden"}
                            </Button>
                        </div>
                    </Form.Item>
                </div>
            </Card>
        );
    };

    // Rabattzeile im Preisblock (PriceBreakdown): „− Name … Betrag ⊗“, darunter der Link für einen (weiteren) Rabatt.
    return (
        <>
            {cardOpen && <DiscountCard />}
            {currentDiscount > 0 && (
                <div className={`${css.line} ${css.deduction}`} data-testid="price-breakdown-discount">
                    <span>
                        {"− "}
                        {/* Rabattcodes (Beta): Einlösung als Tag kenntlich machen, wie in der Buchungsliste. */}
                        {linkedDiscountCode ? (
                            <Tag color="purple" icon={<CheckOutlined />} data-testid="BookingDiscountCodeTag">
                                {currentDiscountName || "Rabattcode"}
                            </Tag>
                        ) : (
                            <span>{currentDiscountName || "Rabatt"}</span>
                        )}
                    </span>
                    <span style={{display: "flex", alignItems: "center"}}>
                        {renderAmount({
                            amount: currentDiscount,
                            currency: currency.short as Currency,
                        })}
                        <Button
                            type="text"
                            size="small"
                            icon={<CloseCircleOutlined style={{color: red[4]}} />}
                            style={{marginLeft: 3}}
                            onClick={async () => {
                                // WP1: siehe Anwenden-Handler — kein optimistisches Setzen vor
                                // regenerateSchedule (sonst halber State bei "Abbrechen" im
                                // Vorkasse-Bestätigungsdialog, der auch beim Entfernen mit
                                // Guthaben + Mehrfachraten greifen kann). discountName: "" wird
                                // explizit übergeben, damit regenerateSchedule den Namen auf dem
                                // Erfolgspfad leert (0 ?? … bzw. "" ?? … sind nicht nullish).
                                if (!linkedDiscountCode) {
                                    await removeDiscount();
                                    return;
                                }
                                confirmRemoveCodeDiscount(currentDiscountName, removeDiscount);
                            }}
                        />
                    </span>
                </div>
            )}
            {/* Rabattcode-Einlösung nicht aufstocken: Speichern mit anderem Betrag löst die Einlösung (booking.service). */}
            {!(linkedDiscountCode && currentDiscount > 0) && (
                <div className={css.line}>
                    <span />
                    <Button type="link" size="small" style={{color: blue[5], paddingRight: 0}} icon={<PlusOutlined />} onClick={() => setCardOpen(true)}>
                        {currentDiscount > 0 ? "Weiteren Rabatt hinzufügen" : "Rabatt hinzufügen"}
                    </Button>
                </div>
            )}
        </>
    );
};

export default memo(BookingDiscount);
